import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Link,
  Container,
  Button,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
} from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { lowerCase, toLower } from "lodash";
import { COLORS } from "../../constants";
import { formatDisplayName } from "../../utils/helpers";

const linkStyles = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: COLORS.themeBlack,
  padding: "1em",
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  lineHeight: "1.6875rem",
  boxSizing: "border-box",
  "&:hover": {
    backgroundColor: COLORS.themeBlack,
    color: COLORS.themeYellow,
  },
};

const DesktopSiteBottombar = ({
  categoriesAndTypes,
  user,
  isAuthenticated,
  isLoading,
}) => {
  const [categoryDropDownOpen, setCategoryDropDownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const dropDownRef = useRef(null);
  const dropdownWrapperRef = useRef(null);

  // Define the specific categories to display (limited to 3)
  const popularCategoryConfig = [
    { slug: "service_parts", title: "Service Parts" },
    { slug: "belts_and_chain_drive_system", title: "Belts" },
    { slug: "fuel_delivery_system", title: "Fuel Pumps" },
  ];

  // Filter categoriesAndTypes to include only the selected categories
  const popularCategories = popularCategoryConfig
    .map((config) => {
      const category = categoriesAndTypes.find((cat) => {
        if (config.slug.includes("/")) {
          const [parent, type] = config.slug.split("/");
          return (
            cat.productCategoryName === parent &&
            cat.productTypes?.some((t) => toLower(t.name) === type)
          );
        }
        return toLower(cat.productCategoryName).replaceAll(" ", "_") === config.slug;
      });

      if (!category) return null;

      return {
        title: config.title,
        slug: config.slug.includes("/")
          ? config.slug.split("/")[0]
          : config.slug,
        image: category.productCategoryImage,
        productTypes: category.productTypes,
          productCategoryId: category.productCategoryId,
      };
    })
    .filter(Boolean);

  const handleCategoryDropDownClick = () => {
    setCategoryDropDownOpen(!categoryDropDownOpen);
    // if (!categoryDropDownOpen) {
    //   setSelectedCategory(categories[0]); // Set default selected category when opening
    // } else {
    //   setSelectedCategory(null);
    // }
  };

  const handleCategoryHover = (category) => {
    setSelectedCategory(category);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropDownRef.current &&
        !dropDownRef.current.contains(event.target) &&
        dropdownWrapperRef.current &&
        !dropdownWrapperRef.current.contains(event.target)
      ) {
        setCategoryDropDownOpen(false);
        setSelectedCategory(null);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        setCategoryDropDownOpen(false);
        setSelectedCategory(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, []);

  if (isLoading) return null;

  // function to locally sort categories alphabetically ASC
  const sortedCategories = [...categoriesAndTypes].sort((a, b) =>
    toLower(a.productCategoryName).localeCompare(toLower(b.productCategoryName))
  );

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: COLORS.secondBackground,
        color: COLORS.darkText,
      }}
    >
      <Container>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Button
              onClick={handleCategoryDropDownClick}
              startIcon={<CategoryIcon />}
              endIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: COLORS.themeYellow,
                color: COLORS.themeBlack,
                fontSize: "0.875rem",
                fontFamily: "Rubik, sans-serif",
                fontWeight: 500,
                padding: "1em",
                textTransform: "uppercase",
                borderRadius: 0,
                display: "flex",
                alignItems: "center",
                lineHeight: "1.6875rem",
                boxSizing: "border-box",
                "&:hover": {
                  backgroundColor: COLORS.themeYellow,
                  color: COLORS.themeBlack,
                },
              }}
              ref={dropDownRef}
            >
              Shop by Category
            </Button>
            {categoryDropDownOpen && (
              <Box
                ref={dropdownWrapperRef}
                sx={{
                  display: "flex",
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  zIndex: 1000,
                  mt: 1,
                }}
              >
                <Paper
                  sx={{
                    width: "300px",
                    height: "400px",
                    overflowY: "auto",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: "var(--border-radius-xs)",
                    backgroundColor: COLORS.white,
                    direction: "rtl",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: COLORS.secondBackground,
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: COLORS.themeBlack,
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: COLORS.themeYellow,
                    },
                  }}
                >
                  <List sx={{ direction: "ltr" }}>
                    {sortedCategories.map((category, i) => {
                      const isSelected =
                        selectedCategory &&
                        selectedCategory.productCategoryName === category.productCategoryName;

                      return (
                        <div
                          key={i}
                          onMouseEnter={() => handleCategoryHover(category)}
                        >
                          <ListItemButton
                            href={`/products/${category.productCategoryId}`}
                            sx={{
                              color: COLORS.darkText,
                              fontFamily: "Rubik",
                              fontWeight: "300",
                              backgroundColor: isSelected
                                ? COLORS.themeBlack
                                : "inherit",
                              "& .MuiTypography-root": {
                                color: isSelected
                                  ? COLORS.themeYellow
                                  : COLORS.darkText,
                              },
                              "&:hover": {
                                backgroundColor: COLORS.themeBlack,
                                "& .MuiTypography-root": {
                                  color: COLORS.themeYellow,
                                },
                              },
                            }}
                          >
                            <ListItemText disableTypography>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 500 }}
                              >
                                {formatDisplayName(category.productCategoryName)}
                              </Typography>
                            </ListItemText>
                          </ListItemButton>
                          {i < categoriesAndTypes.length - 1 && (
                            <Divider
                              component="li"
                              sx={{ borderColor: COLORS.border }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </List>
                </Paper>
                {selectedCategory && (
                  <Paper
                    sx={{
                      width: "500px",
                      height: "400px",
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: "var(--border-radius-xs)",
                      backgroundColor: COLORS.white,
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      ml: 0.5,
                    }}
                  >
                    <Box sx={{ p: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "var(--font-size-xs)",
                          color: COLORS.themeBlack,
                          p: 1,
                        }}
                      >
                        {formatDisplayName(selectedCategory.productCategoryName)}
                      </Typography>
                      <Divider sx={{ mt: 1, mb: 1 }} />
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        flexWrap: "wrap",
                        overflowY: "auto",
                        "&::-webkit-scrollbar": {
                          width: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          background: COLORS.secondBackground,
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: COLORS.themeBlack,
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          background: COLORS.themeYellow,
                        },
                      }}
                    >
                      {[...selectedCategory.productTypes]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((type, j) => {
                          const typeSlug =
                            lowerCase(type.name) === "view all"
                              ? `/products/${selectedCategory.productCategoryId}`
                              : `/products/${selectedCategory.productCategoryId}/${type.productTypeId}`;

                          return (
                            <Box
                              key={j}
                              sx={{
                                minWidth: "150px",
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <ListItemButton
                                href={typeSlug}
                                sx={{
                                  pl: 2,
                                  "&:hover": {
                                    backgroundColor: COLORS.themeBlack,
                                    "& .MuiTypography-root": {
                                      color: COLORS.themeYellow,
                                    },
                                  },
                                }}
                              >
                                <ListItemText disableTypography>
                                  <Typography variant="caption">
                                    {formatDisplayName(type.name)}
                                  </Typography>
                                </ListItemText>
                              </ListItemButton>
                            </Box>
                          );
                        })}
                    </Box>
                    <Box
                      component="img"
                      src="/images/porsche.png"
                      alt="Category Image"
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 8,
                        height: "5.7rem",
                        objectFit: "contain",
                        borderRadius: "2px",
                      }}
                    />
                  </Paper>
                )}
              </Box>
            )}
            <Box sx={{ display: "flex", gap: "0.875rem" }}>
              {popularCategories.map((category, index) => (
                <Link
                  key={index}
                  href={`/products/${category.productCategoryId}`}
                  color="inherit"
                  underline="none"
                  sx={linkStyles}
                >
                  {category.title}
                </Link>
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default DesktopSiteBottombar;
